import { APP_URL } from "@/config/urls";
import { getCookie } from "@/lib/utils";
import { store } from "@/store/store";
import { SHA256 } from "crypto-js";

declare global {
  interface Window {
      dataLayer: any[];
      rudderanalytics: any;
  }
}

export const handleDownload = async (pdfUrl: string, fileName: string) => {
  try {
    const proxyUrl = `${APP_URL.PROXY_API_BASE_URL}?url=${encodeURIComponent(
      pdfUrl
    )}`;
    const response = await fetch(proxyUrl, { method: "GET", mode: "cors" });

    if (!response.ok) {
      throw new Error(`Failed to fetch the file: ${response.statusText}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading PDF:", error);
    alert("An error occurred while downloading the file.");
  }
};

export const getPageIndex = (slug = "") => {
  // const isProduction = process.env["NEXT_PUBLIC_ENV"] === "production";
  // const includesLp = slug.includes("-lp");
  // return isProduction && !includesLp ? "index, follow" : "noindex, nofollow";
  return "noindex, nofollow";
};

// Convert hex color to rgba with given opacity
export const hexToRgba = (hex: string, opacity: number): string => {
  if (!hex) return `rgba(0, 0, 0, ${opacity})`;
  const cleanHex = hex.replace("#", "");
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export function isMobileDevice() {
  if (typeof window === "undefined") return false;
  return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

// keep this helper sync now
export const hashData = (data: string): string => {
  if (!data) return "";
  return SHA256(data.trim().toLowerCase()).toString();
};

/**
 * Push a single named event onto the GTM dataLayer.
 * No-ops during SSR, and creates the dataLayer when GTM has not booted yet so the
 * event is replayed once the container loads.
 */
export const pushGtmEvent = (
  event: string,
  payload: Record<string, unknown> = {},
): void => {
  if (typeof window === "undefined") return;

  const data = {
    event,
    gtm: {
      uniqueEventId: Math.floor(Math.random() * 1000000),
      start: Date.now(),
      priorityId: 11,
    },
    ...payload,
  };

  console.log("GTM Event Pushed:", data);

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
};

export const pushToGTMHashed = (email: string, phone: string, formType: string): void => {
  if (typeof window === "undefined") return;

  const phoneForHash = phone && phone.length === 10 ? "+91" + phone : phone;

  const payload = {
    form_type: formType,
    email_hash: hashData(email),
    phone_hash: hashData(phoneForHash || ""),
    eventCallback: "Function",
  };

  // Same payload under both names: `form_submission` is the long-standing trigger,
  // `lead_submitted` is the conversion event fired alongside it.
  pushGtmEvent("form_submission", payload);
  pushGtmEvent("lead_submitted", payload);
};


export const authSuccess = (props: {
  section: string;
  component: string;
  additionalInfo?: Record<string, unknown>;
  // TODO: Fix this type
  userInfo: {
    id?: 1238;
    area_of_interest?: string;
    university?: string;
    course_id?: null;
    created_at?: null;
    email: string;
    highest_qualification?: string;
    leads_id?: null;
    message?: null;
    mobile?: string;
    name?: string;
    source_campaign?: null;
    source_media?: null;
    source_params?: null;
    source_path?: null;
    source_refer_url?: null;
    source_source?: null;
    specialization_id?: null;
    lead_identifier?: null;
    status?: null;
    uni_code?: null;
  };
  label: string;
  isFirstByUser: boolean;
}) => {
  tracker({
    event: "auth_success",
    data: {
      section: props.section,
      component_name: props.component,
      label: props.label,
      medium: "mobile_number_flow",
      additional_info: JSON.stringify(props.additionalInfo),
      first_by_user: props.isFirstByUser,
      first_by_user_and_program: props.isFirstByUser,
    },
  });
  identify({
    userId: props.userInfo.email,
    traits: {
      name: props.userInfo.name,
      email: props.userInfo.email,
    },
  });
};

interface ITrackerProps {
  event: string;
  data?: Record<string, unknown>;
}

interface ITrackerIdentifyProps {
  userId: string;
  traits: Record<string, unknown>;
}

const generateOfferingKeyIdentifier = (path: string) => {
  let cleanedPath = path.replace(/^\/|\/$/g, "");
  cleanedPath = cleanedPath.replace(/\//g, "_");
  return cleanedPath;
};

interface ISearchParams extends URLSearchParams {
  showMobile?: string;
}

export const detectDevice = (): {
  userAgent: string;
  isDeviceMobile: boolean;
} => {
  if (typeof window === "undefined") {
    return { userAgent: "", isDeviceMobile: false };
  }
  const { userAgent } = window.navigator;

  let isMobile =
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od|ad)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
      userAgent
    ) ||
    /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
      userAgent.substring(0, 4)
    );

  const search: ISearchParams = new URLSearchParams(window.location.search);
  if (search.showMobile === "true") {
    isMobile = true;
  }

  return { userAgent, isDeviceMobile: isMobile };
};

/**
 * First-touch referrer — the external page the user originally arrived from.
 * Captured once per session on first read and persisted in sessionStorage, so
 * it survives in-app navigation and full page reloads. Same-origin referrers
 * are dropped so we keep only the true external source. Returns "" for direct
 * visits (typed URL / bookmark) where the browser exposes no referrer.
 * Truncated to 255 chars as a defensive cap before it's sent as `lead_source_key`.
 */
export const getEntryReferrer = (): string => {
  if (typeof window === "undefined") return "";
  const KEY = "entry_referrer";
  try {
    const stored = sessionStorage.getItem(KEY);
    if (stored !== null) return stored;

    let value = document.referrer || "";
    if (value) {
      try {
        if (new URL(value).origin === window.location.origin) value = "";
      } catch {
        /* not a parseable URL — keep the raw value */
      }
    }
    value = value.slice(0, 255);
    sessionStorage.setItem(KEY, value);
    return value;
  } catch {
    return (document.referrer || "").slice(0, 255);
  }
};

const globalAttr = () => {
  const state = store.getState();
  const urlParams = new URL(window.location.toString()).searchParams;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { userAgentData } = window?.navigator as any;
  const isDeviceMobile = detectDevice();
  const pageCategories = ["home_page", "program_page", "university_page"];
  const offeringKeyIdentifier = pageCategories.includes(state.globalslice.pageCategory)
    ? generateOfferingKeyIdentifier(window.location.pathname)
    : "";
  const userDetails = sessionStorage.getItem("u_details") || "";
  let userEmail = "";
  let leadId = "";
  if (userDetails && userDetails !== "undefined") {
    const parsedDetails = JSON.parse(userDetails);
    userEmail = parsedDetails?.email || "";
    leadId = parsedDetails?.id || "";
  }

  let resolvedPageCategory;

  if (state.globalslice.pageCategory === "home_page") {
    resolvedPageCategory = "home_page";
  } else if (state.globalslice.pageCategory === "program_page") {
    resolvedPageCategory = "course_without_spec_page";
  } else {
    resolvedPageCategory = state.globalslice.pageCategory;
  }

  return {
    business_entity: "cu",
    country: "",
    city: "",
    state: "",
    source_identifier: isDeviceMobile ? "mobile" : "web",
    device_type: isDeviceMobile ? "mobile_web" : "desktop_web",
    platform_os: userAgentData?.platform || "",
    platform_version: userAgentData?.brands?.[1]?.version || "",
    platform_agent: userAgentData?.brands?.[1]?.brand || "",
    session_id: parseInt(getCookie("cu-sessionId") || "0"),
    experiment_name: "",
    page_url: document.URL,
    page_title: document.title,
    screen_resolution: `${window.screen.width}*${window.screen.height}`,
    logged_in_flag: sessionStorage.getItem("isUserVerified"),
    referrer: document.referrer,
    path: window.location.pathname,
    paid_user: "",
    offering_key_identifier: offeringKeyIdentifier,
    page_category: resolvedPageCategory,
    latest_utm_campaign: urlParams.get("utm_campaign") || "",
    latest_utm_medium: urlParams.get("utm_medium") || "",
    latest_utm_source: urlParams.get("utm_source") || "",
    latest_utm_term: urlParams.get("utm_term") || "",
    latest_utm_content: urlParams.get("utm_content") || "",
    university: process.env.NEXT_PUBLIC_UNIVERSITY_NAME || "",
    email_id: userEmail || "",
    lead_identifier: leadId || "",
  };
};


export const tracker = (props: ITrackerProps) => {
  const { event, data } = props;
  if (typeof window !== "undefined") {
    if (window.rudderanalytics) {
      const defaultData = globalAttr();

      const eventFinalData = { ...defaultData, ...data };
      window.rudderanalytics.track(event, eventFinalData, {}, () => {
        // console.info("Event Tracked success", { event, eventFinalData });
      });
    } else {
      console.error("RudderStack Not Loaded");
    }
  }
};

export const identify = (props: ITrackerIdentifyProps): void => {
  const { userId, traits } = props;
  if (typeof window !== "undefined") {
    window?.rudderanalytics?.identify(userId, traits, {}, () => {
    //   console.info("Event Tracked success", { userId, traits });
    });
  }
};

// Derives a soft shadow rgba from the button's own color (keeps the shadow the
// same hue as the button instead of a hardcoded value). Figma falls back to the
// maroon "Apply Now" tint when no button color is configured.
export const shadowFromColor = (color: string, alpha = 0.6): string => {
    if (!color) return "transparent";
    const c = color.trim();
    if (c.startsWith("#")) {
        let hex = c.slice(1);
        if (hex.length === 3)
            hex = hex
                .split("")
                .map((ch) => ch + ch)
                .join("");
        if (hex.length >= 6) {
            const r = parseInt(hex.slice(0, 2), 16);
            const g = parseInt(hex.slice(2, 4), 16);
            const b = parseInt(hex.slice(4, 6), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
        return c;
    }
    const m = c.match(/^rgba?\(([^)]+)\)/i);
    if (m) {
        const [r, g, b] = m[1].split(",").map((p) => p.trim());
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return c;
};

